import React, { useMemo, useState } from 'react';
import diff, { ChangeType } from '@sandboxed/diff';

import styles from './Diff.module.scss';

type Theme = {
  color: string;
  backgroundColor: string;
  accentBackgroundColor?: string;
};

type ChangeTypeTheme = {
  [ChangeType.ADD]: Theme;
  [ChangeType.REMOVE]: Theme;
  [ChangeType.UPDATE]: Theme;
  [ChangeType.NOOP]: Theme;
};

type DiffResult = {
  type: ChangeType;
  str: string;
  depth: number;
  path: Array<string | number>;
};

type DiffProps = {
  oldData: unknown;
  newData: unknown;
  theme?: ChangeTypeTheme;
};

const defaultTheme: ChangeTypeTheme = {
  [ChangeType.ADD]: {
    color: 'var(--st-token-emerald-color)',
    backgroundColor: 'var(--st-token-emerald-bg)',
    accentBackgroundColor: 'var(--st-color-emerald-20)'
  },
  [ChangeType.REMOVE]: {
    color: 'var(--st-token-lava-color)',
    backgroundColor: 'var(--st-token-lava-bg)',
    accentBackgroundColor: 'var(--st-color-lava-20)'
  },
  [ChangeType.UPDATE]: {
    color: 'var(--st-token-tangerine-color)',
    backgroundColor: 'var(--st-token-tangerine-bg)',
    accentBackgroundColor: 'var(--st-color-tangerine-20)'
  },
  [ChangeType.NOOP]: {
    color: 'var(--st-text-color)',
    backgroundColor: 'transparent',
    accentBackgroundColor: 'transparent'
  }
};

const changeSymbols = {
  [ChangeType.ADD]: '+ ',
  [ChangeType.REMOVE]: '- ',
  [ChangeType.UPDATE]: '~ ',
  [ChangeType.NOOP]: '  '
};

const LineComponent = ({ result, theme, number }: { result: DiffResult; theme: ChangeTypeTheme; number: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const themeStyle = theme[result.type];
  const indent = { paddingLeft: `${result.depth * 15}px` };
  const shouldShorten = result.str.length > 100;

  const shortenedStr = shouldShorten
    ? `${result.str.slice(0, 98)}...(${result.str.length - 100})${result.str.slice(-2)}`
    : result.str;

  function handleShortenedStringClick() {
    if (shouldShorten) {
      setIsExpanded(!isExpanded);
    }
  }

  const clickProp = shouldShorten ? { onDoubleClick: handleShortenedStringClick } : {};

  return (
    <tr className={styles.lineWrapper} style={{ backgroundColor: themeStyle.backgroundColor }} {...clickProp}>
      <td
        style={{
          backgroundColor: themeStyle.accentBackgroundColor ?? 'transparent',
          padding: '4px',
          textAlign: 'center'
        }}
      >
        {number}
      </td>
      <td className={styles.lineSymbol}>{changeSymbols[result.type]}</td>
      <td className={styles.lineData} style={{ ...themeStyle, ...indent }}>
        {shouldShorten && !isExpanded ? shortenedStr : result.str}
      </td>
    </tr>
  );
};

const LineGroupComponent = ({
  results,
  currentDepth,
  viewMoreLines = 3,
  theme,
  currentIndex
}: {
  results: DiffResult[];
  currentDepth: number;
  viewMoreLines?: number;
  theme: ChangeTypeTheme;
  currentIndex: number;
}) => {
  const [shownLinesCount, setShownLinesCount] = useState(0);
  const collapsedIndexStart = currentIndex - results.length - viewMoreLines + 1;
  const [currentShownLines, setCurrentShownLines] = useState<DiffResult[]>([]);
  const findIndex = useMemo(() => {
    // find first index that is less than current depth
    const lookForDepth = currentShownLines.at(0)?.depth ?? currentDepth;

    // start from the end of the results array and go backwards (excluding currentShownLines)
    for (let i = results.length - 1 - currentShownLines.length; i >= 0; i--) {
      if (results[i].depth < lookForDepth) {
        return i;
      }
    }

    return -1;
  }, [results, currentDepth, currentShownLines]);

  // on double click, show 10 more lines
  function handleShowMoreLines(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (e.type === 'keyup' && (e as React.KeyboardEvent<HTMLDivElement>).key !== 'Enter') {
      return;
    }

    let start = results.length - (shownLinesCount + 1) * 10;
    let end = start + (shownLinesCount + 1) * 10;

    if (start < 0) {
      start = 0;
      end = results.length;
    }

    setCurrentShownLines(results.slice(start, end));
    setShownLinesCount(shownLinesCount + 1);
  }

  return (
    <>
      {currentShownLines.length !== results.length && (
        <tr className={styles.lineWrapper}>
          <td
            colSpan={3}
            style={{ textAlign: 'center', fontStyle: 'italic', padding: '10px', cursor: 'pointer' }}
            onClick={handleShowMoreLines}
            onKeyUp={handleShowMoreLines}
            tabIndex={0}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
            role="button"
          >
            ... {results.length - currentShownLines.length} unchanged, {collapsedIndexStart}-
            {currentIndex - currentShownLines.length - viewMoreLines} lines hidden. Click to view more ... @@{' '}
            {findIndex < 0 ? '...' : `${collapsedIndexStart + findIndex} @ ${results.at(findIndex)?.str}...`}
          </td>
        </tr>
      )}
      {currentShownLines.map((line, lineIndex) => (
        <LineComponent
          key={`collapsed-${lineIndex}`}
          number={currentIndex - currentShownLines.length - viewMoreLines + lineIndex + 1}
          result={line}
          theme={theme}
        />
      ))}
    </>
  );
};

export const Diff = ({ oldData, newData, theme }: DiffProps) => {
  const diffResult = useMemo(() => diff(oldData, newData), [oldData, newData]);

  const preparedDiff = useMemo(() => {
    let collapsedLines: DiffResult[] = [];
    let noopCounter = 2;
    let groupCount = 0;
    const render: React.ReactNode[] = [];

    const pushCollapsedLines = ({ index }: { index: number }) => {
      // extract at least 3 collapsed lines
      const linesToRender: DiffResult[] = collapsedLines.splice(-3, 3);

      // render previous collapsed lines if any
      if (collapsedLines.length > 0) {
        // Line component for collapsed header
        render.push(
          <LineGroupComponent
            key={`collapsed-group-${groupCount + 1}`}
            results={collapsedLines}
            currentDepth={linesToRender[0].depth}
            viewMoreLines={linesToRender.length}
            theme={theme ?? defaultTheme}
            currentIndex={index}
          />
        );
      }

      // render prev 3 collapsed lines
      linesToRender.forEach((line, lineIndex) => {
        render.push(
          <LineComponent
            key={`collapsed-prev-${groupCount + 1}-${lineIndex}`}
            number={index - linesToRender.length + lineIndex + 1}
            result={line}
            theme={theme ?? defaultTheme}
          />
        );
      });

      collapsedLines = [];
      noopCounter = 0;
      groupCount += 1;
    };

    diffResult.forEach((result, index) => {
      if (result.type === ChangeType.NOOP) {
        // Accumulate collapsed lines after 3 noops
        if (noopCounter > 2) {
          collapsedLines.push(result);
          return;
        }

        noopCounter += 1;
      } else {
        // If there are collapsed lines, push them before rendering the current line
        pushCollapsedLines({ index });
      }

      render.push(
        <LineComponent key={`result-${index}`} number={index + 1} result={result} theme={theme ?? defaultTheme} />
      );
    });

    // Push any remaining collapsed lines at the end
    if (collapsedLines.length > 0) {
      pushCollapsedLines({ index: diffResult.length });
    }

    return render;
  }, [diffResult, theme]);

  return (
    <table className={styles.diffContainer}>
      <colgroup>
        <col width="40" />
        <col />
        <col />
      </colgroup>
      <thead className={styles.lineHeader}>
        <tr>
          <th>line</th>
          <th>changeType</th>
          <th>content</th>
        </tr>
      </thead>
      <tbody>{preparedDiff}</tbody>
    </table>
  );
};
